<template>
  <v-container>
    <back-button :label="transPlural('objectGroup.objectGroup')"></back-button>
    <page-heading>{{ pageTitle }}</page-heading>

    <div class="d-flex justify-end mb-3">
      <delete-button
        @click="showConfirmDeleteGroup = true"
        :disabled="objectGroupSaveLoading"
        v-if="objectGroupId > 0"
      />
    </div>

    <global-validation-errors class="mt-2" :value="validationErrors" />

    <v-form>
      <v-card>
        <v-card-title>
          {{ trans('objectGroup.mainSettings') }}
        </v-card-title>

        <v-card-text>

          <form-input
            :label="trans('objectGroup.name')"
            v-model="objectGroup.name"
            :error-messages="validationErrors.name"
          ></form-input>

          <form-input
            :label="trans('objectGroup.description')"
            v-model="objectGroup.description"
            :error-messages="validationErrors.description"
          ></form-input>

          <form-input
            :label="trans('objectGroup.currency')"
            v-model="objectGroup.currency"
            :error-messages="validationErrors.currency"
            :items="currencies"
            cols="4"
          ></form-input>

          <form-input
            :label="trans('objectGroup.settlementOption')"
            v-model="objectGroup.settlementOption"
            :error-messages="validationErrors.settlementOption"
            :items="objectGroupSettlementOptions"
            cols="4"
          ></form-input>

          <form-input
            :label="trans('objectGroup.vatCommission')"
            v-model="objectGroup.vatCommission"
            :error-messages="validationErrors.vatCommission"
            suffix="%"
            cols="1"
          ></form-input>

          <form-input
            :label="trans('objectGroup.vatRent')"
            v-model="objectGroup.vatRent"
            :error-messages="validationErrors.vatRent"
            suffix="%"
            cols="1"
          ></form-input>

          <form-input
            :label="trans('objectGroup.settlementCutoffDayOfMonth')"
            :help-text="trans('objectGroup.settlementCutoffDayOfMonthHelp')"
            v-model="objectGroup.settlementCutoffDayOfMonth"
            :error-messages="validationErrors.settlementCutoffDayOfMonth"
            :items="settlementCutoffDayOfMonthOptions"
            cols="4"
          ></form-input>

          <form-input
            :label="trans('objectGroup.caretakers')"
            :help-text="trans('objectGroup.caretakersHelp')"
            cols="4"
            optional
          >
            <template v-slot:input>
              <ajax-select
                :value="objectGroup.caretakers"
                @update:value="objectGroup.caretakers = $event"
                item-text="fullName"
                resource-url="users/sub-users"
                dense
                multiple
                :filter-attributes="['firstname', 'surname']"
                :filter-extra="caretakerUsersFilter"
              ></ajax-select>
            </template>
          </form-input>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <cancel-button />
          <save-button
            @click.native="save"
            :disabled="objectGroupSaveLoading"
          />
        </v-card-actions>
      </v-card>

      <v-card class="mt-5">
        <v-card-title>{{ trans('objectGroup.commissionSubheading') }}</v-card-title>

        <v-card-text>
          <form-input
            :label="trans('objectGroup.commission')"
            v-model="objectGroup.commission"
            :error-messages="validationErrors.commission"
            suffix="%"
            cols="1"
          ></form-input>

          <form-input
            :label="trans('objectGroup.basis')"
            v-model="objectGroup.basis"
            :error-messages="validationErrors.basis"
            :items="rentBasisOptions"
            readonly
            cols="4"
          ></form-input>

          <form-input
            :label="trans('objectGroup.commissionIncludeAdditional')"
            v-model="objectGroup.additional"
            :error-messages="validationErrors.additional"
            type="checkbox"
            cols="1"
          ></form-input>

          <form-input
            :label="trans('objectGroup.commissionIncludePets')"
            v-model="objectGroup.pets"
            :error-messages="validationErrors.pets"
            type="checkbox"
            cols="1"
          ></form-input>

          <form-input
            :label="trans('objectGroup.commissionBasis')"
            v-model="objectGroup.net"
            :error-messages="validationErrors.net"
            :items="amountsNetOptions"
            cols="4"
          ></form-input>

          <form-input
            :label="trans('objectGroup.commissionAdd')"
            v-model="objectGroup.commissionAdd"
            :error-messages="validationErrors.commissionAdd"
            cols="4"
          >
            <div slot="input">
              <v-row>
                <v-col class="py-0" cols="4">
                  <currency-input
                    v-model="objectGroup.commissionFee"
                    :currency="objectGroup.currency"
                  ></currency-input>
                </v-col>
                <v-col class="py-0" cols="8">
                  <v-select
                    v-model="objectGroup.commissionAdd"
                    :items="commissionAddOptions"
                    readonly
                  ></v-select>
                </v-col>
              </v-row>
            </div>
          </form-input>

          <form-input
            :label="trans('objectGroup.finalCleaningDefault')"
            v-model="objectGroup.finalCleaning"
            :error-messages="validationErrors.finalCleaning"
            :currency="objectGroup.currency"
            cols="2"
          ></form-input>

          <form-input
            :label="trans('objectGroup.datevAccount')"
            v-model="objectGroup.datevAccount"
            :error-messages="validationErrors.datevAccount"
            cols="2"
          ></form-input>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <cancel-button />
          <save-button
            @click.native="save"
            :disabled="objectGroupSaveLoading"
          />
        </v-card-actions>
      </v-card>

      <v-card class="mt-5">
        <v-card-title>
          {{ trans('objectGroup.settlementSettings') }}
        </v-card-title>

        <settlement-page-setup
            :pages.sync="objectGroup.pages"
        />
        <v-card-actions>
          <v-spacer></v-spacer>
          <cancel-button />
          <save-button
            @click.native="save"
            :disabled="objectGroupSaveLoading"
          />
        </v-card-actions>
      </v-card>

    </v-form>

    <confirm-deletion-modal
      :show-modal.sync="showConfirmDeleteGroup"
      @confirm="deleteGroup"
      :title="trans('objectGroup.delete')"
    >
      {{ trans('objectGroup.confirmDelete', {name: objectGroupName}) }}
    </confirm-deletion-modal>
  </v-container>
</template>

<script>
import PageHeading from '../components/PageHeading';
import {LocaleUtils} from '../utils/locale-utils';
import FormInput from '../components/FormInput';
import CurrencyInput from '../components/CurrencyInput';
import SaveButton from '../components/SaveButton';
import {MainUtils} from '../utils/main-utils';
import {RouterUtils} from '../utils/router-utils';
import CancelButton from '../components/CancelButton';
import SettlementPageSetup from '@/components/SettlementPageSetup';
import AjaxSelect from '../components/AjaxSelect';
import BackButton from '../components/BackButton';
import {comparison, eq} from 'rsql-builder';
import {UserRole} from '../lib/user-roles';
import {ObjectGroupApi} from '../api/object-group-api';
import GlobalValidationErrors from '../components/GlobalValidationErrors';
import {objectGroupSettlementOptions} from '../lib/object-group-settlement-options';
import DeleteButton from '@/components/DeleteButton.vue';
import ConfirmDeletionModal from '@/components/ConfirmDeletionModal.vue';

const basicCurrencies = [
  {value: 'EUR', text: 'Euro (EUR)'},
  {value: 'GBP', text: 'Britische Pfund (GBP)'},
  {value: 'USD', text: 'US Dollar (USD)'},
  {value: 'CHF', text: 'Schweizer Franken (CHF)'},
];

export default {
  name: 'ObjectGroupPage',
  mixins: [
    LocaleUtils,
    MainUtils,
    RouterUtils
  ],
  components: {
    ConfirmDeletionModal,
    DeleteButton,
    GlobalValidationErrors,
    BackButton,
    AjaxSelect,
    SettlementPageSetup,
    CancelButton,
    SaveButton,
    CurrencyInput,
    FormInput,
    PageHeading
  },
  props: ['objectGroupId'],
  data: () => ({
    objectGroup: {},
    objectGroupName: null,
    currencies: basicCurrencies,
    objectGroupSaveLoading: false,
    validationErrors: {},
    objectGroupSettlementOptions,
    showConfirmDeleteGroup: false
  }),

  computed: {
    monthOptions () {
      let monthOptions = [];

      for (let month = 1; month <= 12; month++) {
        monthOptions.push({
          value: month,
          text: this.$t('general.monthNames['+(month-1)+']')
        })
      }

      return monthOptions;
    },
    yearOptions () {
      let yearOptions = [];
      let currentYear = new Date().getFullYear();

      for (let year = 2010; year <= currentYear + 5; year++) {
        yearOptions.push({
          value: year,
          text: year
        })
      }

      return yearOptions;
    },
    settlementCutoffDayOfMonthOptions () {
      let settlementCutoffDayOfMonthOptions = [
        {
          value: 31,
          text: this.trans('objectGroup.settlementCutoffDayOfMonthDoNotShift')
        }
      ];

      for (let day = 30; day >= 10; day--) {
        settlementCutoffDayOfMonthOptions.push({
          value: day,
          text: day + '.'
        });
      }

      return settlementCutoffDayOfMonthOptions;
    },
    rentBasisOptions () {
      return [
        {value: 'rental_fee', text: this.trans('objectGroup.rentBasisRent')}
      ];
    },
    commissionAddOptions () {
      return [
        {value: 1, text: this.trans('objectGroup.settlementCommissionAddPerBooking')}
      ];
    },
    amountsNetOptions () {
      return [
        {value: false, text: this.trans('objectGroup.settlementAmountOptionGross')},
        {value: true, text: this.trans('objectGroup.settlementAmountOptionNet')}
      ];
    },
    caretakerUsersFilter () {
      return [comparison('role', eq(UserRole.CARETAKER))];
    },
    pageTitle () {
      if (this.objectGroupId) {
        return this.objectGroupName;
      } else {
        return this.trans('objectGroup.new');
      }
    }
  },

  mounted () {
    if (this.objectGroupId) {
      this.getObjectGroup();
    } else {
      this.objectGroup = {
        currency: 'EUR',
        vatCommission: 19,
        vatRent: 7,
        basis: 'rental_fee',
        net: false,
        commissionAdd: 1,
        commission: 0,
        commissionFee: 0,
        gas: 0,
        water: 0,
        electricity: 0,
        finalCleaning: 0
      };
    }
  },

  methods: {
    getObjectGroup () {
      this.$http.get('object-groups/' + this.objectGroupId)
      .then(response => {
        this.objectGroup = response.data;
        this.objectGroup.caretakers = this.objectGroup.caretakers ? this.objectGroup.caretakers.map(userCaretaker => userCaretaker.id) : [];
        this.objectGroupName = this.objectGroup.name;
      });
    },

    save () {
      this.objectGroupSaveLoading = true;
      let request;
      if (this.objectGroupId) {
        request = ObjectGroupApi.update(this.objectGroupId, this.objectGroup);
      } else {
        request = ObjectGroupApi.create(this.objectGroup);
      }

      request.then(response => {
        this.validationErrors = {};
        this.showSavedSuccessfullyNotification();
        this.replaceToRoute('ObjectGroupsPage');
      })
      .catch((response) => this.validationErrors = this.getValidationErrors(response))
      .finally(() => {
        this.objectGroupSaveLoading = false;
      });
    },

    deleteGroup () {
      this.objectGroupSaveLoading = true;
      ObjectGroupApi.delete(this.objectGroupId)
        .then(response => {
          this.showDeletedSuccessfullyNotification();
          this.replaceToRoute('ObjectGroupsPage');
        })
        .catch(this.handleErrorResponse)
        .finally(() => {
          this.objectGroupSaveLoading = false;
        });
    }
  }
}
</script>

<style>
.row-object {
  background-color: #F5F5F5;
}
</style>
